import React from 'react';

const RHResultCard = ({ result }) => {
  if (!result) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <p className="text-gray-500 text-center">Calcul non encore effectué</p>
      </div>
    );
  }

  const rte = Number(result.rte);
  const pa = Number(result.pa);
  const d = Number(result.d);
  const rh_score = Number(result.rh_score);
  const bradford_total = result.bradford_total;
  const bradford_mandatory_met = result.bradford_mandatory_met;
  const attribution = result.attribution;
  const temps_perdu_heures = Number(result.temps_perdu_heures);
  const confidence = result.confidence;
  const control_reference = result.control_reference;
  const calculated_at = result.calculated_at;

  // Calculate time conversions
  const jours = (temps_perdu_heures / 8).toFixed(2);
  const annees = (temps_perdu_heures / 8 / 220).toFixed(2);

  // Format calculated_at
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return `${date.toLocaleDateString('fr-FR')} ${date.toLocaleTimeString('fr-FR')}`;
    } catch {
      return dateString;
    }
  };

  // Get attribution badge colors
  const getAttributionColors = (attr) => {
    switch (attr) {
      case 'attribuable':
        return 'bg-green-100 text-green-800 border border-green-300';
      case 'incertain':
        return 'bg-orange-100 text-orange-800 border border-orange-300';
      case 'non_attribuable':
        return 'bg-red-100 text-red-800 border border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-300';
    }
  };

  // Get confidence badge colors
  const getConfidenceColors = (conf) => {
    switch (conf) {
      case 'elevee':
        return 'bg-green-100 text-green-700';
      case 'moyenne':
        return 'bg-yellow-100 text-yellow-700';
      case 'basse':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Gauge component
  const Gauge = ({ label, value, color }) => (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-sm font-semibold text-gray-900">{value.toFixed(2)}</span>
      </div>
      <div className="bg-gray-200 h-3 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all duration-300`}
          style={{ width: `${Math.min(value * 100, 100)}%` }}
        />
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Composantes Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Composantes</h3>
        <Gauge label="RTE (Taux d'Exposition)" value={rte} color="bg-blue-500" />
        <Gauge label="PA (Puissance d'Attribution)" value={pa} color="bg-green-500" />
        <Gauge label="D (Durée)" value={d} color="bg-purple-500" />
      </div>

      {/* Score RH Section */}
      <div className="border-t border-gray-100 mt-4 pt-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Score RH</h3>
        <div className="text-center mb-4">
          <div className="text-5xl font-bold text-gray-900">{rh_score.toFixed(2)}</div>
          <p className="text-sm text-gray-500 mt-2">
            RTE × PA × D = {rte.toFixed(2)} × {pa.toFixed(2)} × {d.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Temps Perdu Section */}
      <div className="border-t border-gray-100 mt-4 pt-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Temps Perdu</h3>
        <p className="text-gray-700">
          <span className="font-semibold">{temps_perdu_heures.toFixed(2)}h</span>
          {' = '}
          <span className="font-semibold">{jours} jours</span>
          {' = '}
          <span className="font-semibold">{annees} années</span>
        </p>
      </div>

      {/* Attribution Section */}
      <div className="border-t border-gray-100 mt-4 pt-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Attribution</h3>
        <div className="mb-3">
          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getAttributionColors(attribution)}`}>
            {attribution === 'attribuable' && 'Attribuable'}
            {attribution === 'incertain' && 'Incertain'}
            {attribution === 'non_attribuable' && 'Non attribuable'}
          </span>
        </div>
        <p className="text-sm text-gray-600">
          {bradford_mandatory_met}/5 obligatoires • {bradford_total}/9 total
        </p>
      </div>

      {/* Confiance Section */}
      <div className="border-t border-gray-100 mt-4 pt-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Confiance</h3>
        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getConfidenceColors(confidence)}`}>
          {confidence === 'elevee' && 'Élevée'}
          {confidence === 'moyenne' && 'Moyenne'}
          {confidence === 'basse' && 'Basse'}
        </span>
      </div>

      {/* Référence Contrôle Section */}
      {control_reference && (
        <div className="border-t border-gray-100 mt-4 pt-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Référence Contrôle</h3>
          <p className="text-sm text-gray-700">{control_reference}</p>
        </div>
      )}

      {/* Date de Calcul */}
      <div className="border-t border-gray-100 mt-4 pt-4">
        <p className="text-xs text-gray-400">
          Calculé le {formatDate(calculated_at)}
        </p>
      </div>
    </div>
  );
};

export default RHResultCard;
